import useSWR from "swr";
import { addDays, format, startOfWeek } from "date-fns";
import { fetcher } from "../fetcher";
import { CafeHoursModel, CafeHoursData } from "./models";

export type CafeHoursParams = {
  branchNumber?: string | null;
};

type DayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

function getDateOfDay(dateInWeek: Date, desiredDayIndex: DayIndex): Date {
  const monday = startOfWeek(dateInWeek, {
    weekStartsOn: 1,
  });

  return addDays(monday, desiredDayIndex);
}

export function useCafeHours({
  branchNumber,
}: CafeHoursParams): CafeHoursModel[] {
  if (!branchNumber) {
    // eslint-disable-next-line no-console
    console.error(
      "Cafe Hours Error: Cafe has an invalid branch number. Check configuration.",
    );
    return [];
  }

  const base = process.env.NEXT_PUBLIC_CLOUDFLARE_WORKER_API_HOST || "";
  const endpoint = `${base}/api/v2/cafes/hours?branchNumber=${branchNumber}`;

  const { data: response } = useSWR(
    [endpoint],
    (url) => fetcher(url) as Promise<CafeHoursData[]>,
  );

  const maybeFullWeekHours =
    response?.map((cafeHours) => new CafeHoursModel(cafeHours)) ?? [];

  const mondayThroughSundayDayOfWeekIndex: DayIndex[] = [0, 1, 2, 3, 4, 5, 6];

  const dateInWeek =
    maybeFullWeekHours.find((model) => !!model.date)?.date ?? new Date();
  const timezone = maybeFullWeekHours[0]?.timezone ?? "UTC";

  return mondayThroughSundayDayOfWeekIndex.map((weekDayIndex) => {
    const matchingDay = maybeFullWeekHours.find(
      ({ day: cafeDay }) => cafeDay === weekDayIndex,
    );

    return (
      matchingDay ??
      new CafeHoursModel({
        date: format(getDateOfDay(dateInWeek, weekDayIndex), "yyyy-MM-dd"),
        closes: null,
        opens: null,
        timezone,
      })
    );
  });
}
